import { useLocation } from "wouter";
import {
  API_BASE,
  useAuthMe,
  usePublishedProjects,
  type ProjectType,
  type PublishedProject,
} from "@vorloop/api-client-react";
import { NavRail } from "../components/layout/NavRail";
import { WorkspaceSwitcher } from "../components/layout/WorkspaceSwitcher";

/**
 * Projects with an active publish slug. Joined server-side (no N+1) —
 * see GET /api/projects/published.
 */
export function Published() {
  const me = useAuthMe();
  const handle = me.data?.user?.handle ?? "you";
  const list = usePublishedProjects();

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: "var(--bg-canvas)" }}>
      <NavRail handle={handle} />
      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "10px 24px",
          }}
        >
          <WorkspaceSwitcher handle={handle} compact />
        </header>

        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            padding: "32px 32px 80px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 960, display: "flex", flexDirection: "column", gap: 18 }}>
            <header>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.01em" }}>
                Published projects
              </h1>
              <p style={{ color: "var(--text-tertiary)", fontSize: 13, marginTop: 6 }}>
                Live deployments served from <code style={{ fontFamily: "var(--font-mono)" }}>preview.vor.codes</code>.
              </p>
            </header>

            {list.error && (
              <ErrorPanel>{list.error.message}</ErrorPanel>
            )}

            {list.isLoading && (
              <div style={{ color: "var(--text-tertiary)", fontSize: 13 }}>Loading…</div>
            )}

            {!list.isLoading && !list.error && (list.data?.items.length ?? 0) === 0 && (
              <EmptyState />
            )}

            {(list.data?.items.length ?? 0) > 0 && (
              <ol
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {list.data!.items.map((p) => (
                  <PublishedRow key={p.id} project={p} />
                ))}
              </ol>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function PublishedRow({ project }: { project: PublishedProject }) {
  const [, setLocation] = useLocation();
  // Same trap as the preview iframe: in prod the SPA lives on vor.codes
  // but the publish endpoint lives on api.vor.codes. Prefix API_BASE so
  // the link opens the actual published bundle, not the SPA's catch-all.
  const slugUrl = `${API_BASE}/api/published/${project.slug}/`;
  return (
    <li>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          width: "100%",
          padding: "12px 14px",
          background: "var(--bg-panel)",
          border: "1px solid var(--border-default)",
          borderRadius: 10,
          color: "var(--text-primary)",
        }}
      >
        <button
          type="button"
          onClick={() => setLocation(`/editor/${project.id}`)}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            padding: 0,
            color: "var(--text-primary)",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {project.name}
          </div>
          <a
            href={slugUrl}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "inline-block",
              marginTop: 2,
              color: "var(--accent-blue)",
              fontSize: 12,
              fontFamily: "var(--font-mono)",
              textDecoration: "none",
            }}
          >
            {project.slug}
          </a>
        </button>
        <VisibilityPill visibility={project.visibility} />
        <TypePill type={project.type} />
        <span style={{ color: "var(--text-tertiary)", fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
          {relativeDate(project.publishedAt)}
        </span>
      </div>
    </li>
  );
}

function TypePill({ type }: { type: ProjectType }) {
  const tone: Record<ProjectType, { bg: string; fg: string }> = {
    mobile: { bg: "rgba(99,102,241,0.12)", fg: "#a5b4fc" },
    web: { bg: "rgba(16,185,129,0.12)", fg: "#34d399" },
    component: { bg: "rgba(245,158,11,0.12)", fg: "#fbbf24" },
    backend: { bg: "rgba(248,113,113,0.12)", fg: "#fca5a5" },
  };
  const t = tone[type];
  return (
    <span
      style={{
        padding: "2px 8px",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        background: t.bg,
        color: t.fg,
        borderRadius: 999,
      }}
    >
      {type}
    </span>
  );
}

function VisibilityPill({ visibility }: { visibility: "public" | "private" }) {
  const tone =
    visibility === "public"
      ? { bg: "rgba(16,185,129,0.12)", fg: "#34d399" }
      : { bg: "rgba(107,114,128,0.18)", fg: "#cbd5e1" };
  return (
    <span
      style={{
        padding: "2px 8px",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        background: tone.bg,
        color: tone.fg,
        borderRadius: 999,
      }}
    >
      {visibility}
    </span>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        border: "1px dashed var(--border-default)",
        borderRadius: 12,
        padding: 36,
        textAlign: "center",
        color: "var(--text-tertiary)",
        fontSize: 13,
      }}
    >
      <p style={{ margin: 0 }}>Nothing published yet.</p>
      <p style={{ margin: "6px 0 0" }}>
        Open a project and hit <strong>Publish</strong> in the top bar to get a shareable URL.
      </p>
    </div>
  );
}

function ErrorPanel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 12,
        background: "rgba(239,68,68,0.08)",
        border: "1px solid rgba(239,68,68,0.4)",
        borderRadius: 8,
        color: "var(--accent-red)",
        fontSize: 12,
      }}
    >
      {children}
    </div>
  );
}

function relativeDate(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return new Date(iso).toLocaleDateString();
}
